import Backbone from 'backbone-model';
import invariant from 'invariant';
import { isString } from 'lodash';
import URI from 'urijs';

import GroupMemberships from 'collections/GroupMemberships';
import Avatar from 'entities/Avatar';
import { GroupTarget, TARGET_TYPES } from 'entities/Message';
import Organization from 'entities/Organization';
import { getAvatarUrlFromKey } from 'lib/Avatars';
import Entity from 'lib/Entity';
import API from 'lib/RemindAPI';
import FeatureStore from 'stores/FeatureStore';
import { Roles } from 'user/UserConstants';

import { Backbone$Attrs, Backbone$Id } from 'types/typescript/Backbone';
import { LinkClassToOrgRequestStatus } from 'globalTypes';
import { getApolloClientSingleton } from 'lib/apollo/client/browser';
import NAVIGATION_LIST from 'components/Navigation/NavigationList.gql';
import {
  NavigationList,
  NavigationListVariables,
} from 'components/Navigation/__generated__/NavigationList';

export const GROUP_MEMBER_PAGE_SIZE = 200;
export const DEFAULT_COLOR = '#2f75c1';

// https://github.com/remind101/r101-api/blob/master/app/api/v2/entities/medium.rb
export type MediumInfoType = {
  raw: string;
  uri: string;
  pretty: string;
};

// https://github.com/remind101/r101-api/blob/426f7353ce7d9e8834e5792c5372ab6af3f573d9/app/api/v2/entities/group.rb#L73-L86
export type SubscribeInfoType = {
  sms?: MediumInfoType;
  sms_alternate?: MediumInfoType;
  email: MediumInfoType;
};

export type RoleInfoType = {
  role: string;
  count: number;
};

export type LoadGroupMembershipsParams = {
  page?: number;
  perPage?: number;
  query?: string;
  role?: string;
  userIds?: Backbone$Id[];
};

export default class Group extends Entity {
  initialize() {
    this.urlRoot = '/classes';
  }

  defaults() {
    return {
      class_name: '',
      group_avatar: null,
      has_children: false,
      memberships_count: null,
      messages_count: 0,
      name: null,
      // Part of the spec is that this defaults to true when there's no permissions sent from backend
      permissions: { can_join: true },
      possessive_name: '',
      subscriber_initiated_chats: true,
      searchable: true,
      type: '',
      owners: [],
      organization: null,
      roles: [],
      request_to_join_enabled: false,
    };
  }

  parse(response: any) {
    const parsed = Object.assign({}, response);

    const groupAvatar = response['group_avatar'] || response.groupAvatar;
    if (groupAvatar) {
      parsed['group_avatar'] = Avatar.fromJSON(groupAvatar);
    }

    if (response.groupAvatar) {
      parsed['group_avatar'] = Avatar.fromJSON(response.groupAvatar);
    }

    if (response['created_at']) {
      parsed['created_at'] = new Date(response['created_at']);
    }

    if (response['owners']) {
      parsed['owners'] = GroupMemberships.fromJSON(response['owners']);
    }

    if (response['members']) {
      parsed['members'] = GroupMemberships.fromJSON(response['members']);
    }

    if (response['organization']) {
      parsed['organization'] = Organization.fromJSON(response['organization']);
    }

    if (response['archived_at']) {
      parsed['archived_at'] = new Date(response['archived_at']);
    }

    if (response.className) {
      parsed['class_name'] = response.className;
      parsed.className = response.className;
    }

    return parsed;
  }

  // @ts-expect-error ts-migrate(2425) FIXME: Class 'Entity' defines instance member property 'u... Remove this comment to see the full error message
  url(): string {
    if (!this.id) {
      const code = this.getCodeOrError();
      // $FlowFixMe
      return `${this.urlRoot}/@${code}`;
    }
    return super.url();
  }

  webUrl(): string {
    const code = this.getCodeOrError();
    return `/classes/${code}`;
  }

  isRoot(): boolean {
    return false;
  }

  isOrgAdmin(): boolean {
    return this.get('isOrgAdmin');
  }

  getId(): string {
    const getId = this.get('id');
    if (getId) {
      return getId;
    } else {
      return this.id;
    }
  }

  getUuid(): string {
    return this.get('uuid');
  }

  getCreatedAt(): Date {
    return this.get('created_at');
  }

  getName(): string {
    const class_name = this.get('class_name');
    if (isString(class_name)) {
      return class_name;
    }
    return this.get('className');
  }

  getOwners(): GroupMemberships | [] {
    return this.get('owners');
  }

  getMembers(): GroupMemberships | [] {
    return this.get('members');
  }

  // XXX should be immutable once created
  // TODO move into new Group arguments at caller
  setName(name: string): void {
    this.set('class_name', name);
  }

  getCode(): string | undefined {
    return this.get('name');
  }

  getCodeOrError(): string {
    const code = this.getCode();
    if (code) {
      return code;
    } else {
      console.warn({ group: this.attributes });
      console.error('Group getCodeOrError() falling back to "error"');
      return 'error';
    }
  }

  getPdfUrl(locale?: string): string {
    const pdfUrl = this.get('pdf_url');
    invariant(pdfUrl, 'missing pdf_url in Group');
    if (locale) {
      const uri = new URI(pdfUrl);
      uri.addQuery('locale', locale);
      return uri.toString();
    } else {
      return pdfUrl;
    }
  }

  getJoinUrl(): string {
    const joinUrl = this.get('join_url');
    invariant(joinUrl, 'missing join_url in Group');
    return joinUrl;
  }

  hasChildren(): boolean {
    // Property is nullable on server-side, so ensure this is boolean
    return !!this.get('has_children');
  }

  setHasChildren(hasChildren: boolean): void {
    this.set('has_children', hasChildren);
  }

  hasStudentOrParentOwner(): boolean {
    return !!(this.getOwners() as GroupMemberships).some((p) => {
      const role = p.getUser() ? p.getUser().getRole() : '';
      return role === Roles.parent || role === Roles.student;
    });
  }

  getMembershipsCount(): number {
    return this.get('memberships_count') || 0;
  }

  getMembershipCountText(): string {
    const memberCount = this.getMembershipsCount();
    return memberCount === 1 ? '1 person' : `${memberCount} people`;
  }

  getOwnersCount(): number {
    return this.getOwners().length;
  }

  getSubscriptionInfo(): SubscribeInfoType {
    return this.get('subscribe');
  }

  // This group may support short codes, but not if the current user is in Canada
  useShortCodeForUser(user: { getCountryCode: () => string }) {
    return this.getSubscriptionInfo().sms && user.getCountryCode() === 'US' && this.hasShortCode();
  }

  // This group may support SMS subscription, but not if the current user is in Afghanistan
  smsEnabledForUser(user: { getCountryCode: () => string }) {
    return this.getSubscriptionInfo().sms && ['US', 'CA'].includes(user.getCountryCode());
  }

  getType(): string {
    return this.get('type');
  }

  isRequestToJoinEnabled(): boolean {
    return this.get('request_to_join_enabled') || false;
  }

  isStaffClass(): boolean {
    return !this.isAClass();
  }

  isAClass() {
    return this.getType() !== 'organization' && this.getType() !== 'district';
  }

  getStaffClassOrgId(): number | undefined | null {
    return this.get('staff_class_org_id');
  }

  getOrganization(): Organization | undefined {
    return this.get('organization');
  }

  setOrganization(o: Organization): void {
    this.set('organization', o);
  }

  getMessagesCount(): number {
    return this.get('messages_count');
  }

  getLastReadSeq(): number | undefined {
    return this.get('last_read_seq');
  }

  canSubscribersInitiateChats(): boolean {
    return this.get('subscriber_initiated_chats');
  }

  setCanSubscribersInititateChats(canSubscribersInitiateChats: boolean) {
    this.set('subscriber_initiated_chats', canSubscribersInitiateChats);
  }

  hasJoinCode(): boolean {
    return !this.isStaffClass() && this.getType() !== 'root';
  }

  getPermissions(): any {
    return this.get('permissions');
  }

  canJoin(): boolean {
    return !!this.getPermissions()['can_join'];
  }

  isOwnedByUser(userId: Backbone$Id): boolean {
    if (userId) {
      return (this.getOwners() as GroupMemberships)
        .map((o) => o.getUserId())
        .includes(parseInt(userId.toString()));
    }
    return false;
  }

  canEdit(): boolean {
    return this.getPermissions()['can_edit'];
  }

  canLeave(): boolean {
    return this.getPermissions()['can_leave'];
  }

  canAddOwner(): boolean {
    return this.canEdit() && !this.isStaffClass();
  }

  /**
   * @deprecated use postable roles instead
   */
  canPost(): boolean {
    return !!this.getPermissions()['can_post'];
  }

  canDelete(): boolean {
    return !!this.getPermissions()['can_delete'];
  }

  canAddPeople(): boolean {
    return !!this.getPermissions()['can_add_people'] || this.attributes.can_add_people;
  }

  needsK12SenderVerification(): boolean {
    return this.attributes.needsK12SenderVerification;
  }

  getPostableRoles(): string[] {
    const threads = [...(this.getPermissions().postable_roles || [])];

    return threads;
  }

  isSMSEnabled(): boolean {
    // while debugging https://my.trackjs.com/messages/filtered?hash=1eb254c56674faa5
    // https://my.trackjs.com/messages/filtered?hash=43b3602fb3e91315
    // https://github.com/remind101/r101-frontend/commit/60715a0698224de39a5000dcd2b550e5e835ea74
    // Attributes are Group defaults, possibly because the Group entity is being accessed
    // before the group is loaded https://my.trackjs.com/shared/NzE3ZTI4NGY1NmJmNGQ1Y2E3OTA1ODNjNGVhNjAwZmQ
    // We recover with the catch, but what else is missing from the UI that we're not detecting?
    try {
      const { sms } = this.getSubscriptionInfo();
      return !!sms;
    } catch (e) {
      console.warn('Group attributes', this.attributes);
      console.error('Group.isSMSEnabled() failed');
      return false;
    }
  }

  hasShortCode(): boolean {
    const { sms } = this.getSubscriptionInfo();
    return !!sms && sms['raw'].length === 5;
  }

  getPossessiveName(): string {
    return this.get('possessive_name');
  }

  // @return string like "B. McGriff, B. McGriff, L. Feringa, and Mr Wu's "
  getPossessiveSignature(): string {
    // TODO XXX have API send this
    // For now, infer it from possessive_name that API does send like this:
    // https://github.com/remind101/r101-api/blob/426f7353ce7d9e8834e5792c5372ab6af3f573d9/app/decorators/group_decorator.rb#L18-L21
    const sigPlusName = this.getPossessiveName();
    const startOfName = sigPlusName.indexOf(this.getName());
    return sigPlusName.substr(0, startOfName - 1); // minus one for the space
  }

  getSMSNumber(): string | null {
    const { sms } = this.getSubscriptionInfo();
    if (sms) {
      return sms.pretty;
    }
    return null;
  }

  getSMSAlternateNumber(): string | null {
    const { sms_alternate: smsAlt } = this.getSubscriptionInfo();
    if (smsAlt) {
      return smsAlt.pretty;
    }

    return null;
  }

  getEmail(): string | null {
    return this.getSubscriptionInfo()['email']['pretty'];
  }

  isSearchable(): boolean {
    return this.get('searchable');
  }

  setSearchable(value: boolean): Backbone.Model {
    return this.set('searchable', value);
  }

  getAvatarId(): number | undefined {
    const avatar = this.get('group_avatar');
    return avatar ? avatar.id : null;
  }

  getAvatar(): Avatar | undefined {
    return this.get('group_avatar');
  }

  getAvatarUrl(): string | undefined {
    const avatar = this.getAvatar();
    return avatar ? avatar.getUrl() : getAvatarUrlFromKey(this.id);
  }

  getAccentColor(): string {
    const avatar = this.getAvatar();

    if (avatar && avatar.getColor()) {
      return `#${avatar.getColor()}`;
    }

    return DEFAULT_COLOR;
  }

  setAvatar(avatar: Avatar): void {
    this.set('group_avatar', avatar);
  }

  setLastReadSeq(seq: number): void {
    this.set('last_read_seq', seq);
  }

  getRoles(): RoleInfoType[] {
    return this.get('roles');
  }

  // XXX .state is only set client side, should be in store instead of entity
  getState(): string {
    return this.get('state');
  }
  setState(state: Object): void {
    this.set({ state });
  }

  isAvailable(): boolean {
    return this.getState() === 'available';
  }

  isSubscribed(): boolean {
    return this.getState() === 'subscribed';
  }

  isPending(): boolean {
    return this.getState() === 'pending';
  }

  setThreadPreview(role: string, preview: string, sentAt: Date): void {
    if (FeatureStore.hasFeature('classes_v2_removal.web.aug-19')) {
      return;
    }
    const thread = this.get('threads').find((t: any) => t.role === role);
    thread.preview = {
      preview,
      sent_at: sentAt,
    };
  }

  getThreads(): GroupTarget[] {
    if (FeatureStore.hasFeature('classes_v2_removal.web.aug-19')) {
      return [];
    }
    let result = [];
    const targets = this.get('threads');

    if (targets) {
      result = targets.map((t: any) => {
        return { type: TARGET_TYPES.GROUP_TARGET, ...t };
      });
    }

    return result;
  }

  getPostableThreads(): GroupTarget[] {
    if (FeatureStore.hasFeature('classes_v2_removal.web.aug-19')) {
      return [];
    }

    const postableRoles = this.getPostableRoles();
    return this.getThreads().filter((t) => !!postableRoles.includes(t.role));
  }

  getThread(role: string): GroupTarget | undefined {
    if (FeatureStore.hasFeature('classes_v2_removal.web.aug-19')) {
      return undefined;
    } else {
      return this.getThreads().find((t) => t.role === role);
    }
  }

  getLinkClassToOrgRequestStatus(): LinkClassToOrgRequestStatus | null {
    return this.get('linkClassToOrgRequestStatus');
  }

  loadPotentialOwners(
    page: number = 1,
    perPage: number = GROUP_MEMBER_PAGE_SIZE,
    query: string = ''
  ): Promise<GroupMemberships> {
    return API.request(`${this.url()}/potential_owners`, 'GET', {
      page,
      per_page: perPage,
      q: query,
    }).then((response) => GroupMemberships.fromJSON(response.potential_owners) as GroupMemberships);
  }

  loadGroupMemberships(params: LoadGroupMembershipsParams): Promise<GroupMemberships> {
    const parameters = {
      page: 1,
      perPage: GROUP_MEMBER_PAGE_SIZE,
      ...params,
    };

    const options = {
      per_page: parameters.perPage,
      user_ids: parameters.userIds,
      role: parameters.role,
      page: parameters.page,
      q: parameters.query,
    };

    const resourceId = this.id || this.getUuid();
    invariant(resourceId, 'loadGroupMemberships missing resourceId');
    return API.request(`/classes/${resourceId}/members`, 'GET', options).then(
      (response) => GroupMemberships.fromJSON(response.members) as GroupMemberships
    );
  }

  promoteGroupMemberships(memberIds: Backbone$Id[]): Promise<GroupMemberships> {
    return API.request(`${this.url()}/members`, 'PATCH', {
      members: memberIds.map((id) => {
        return {
          id,
          role: 'owner',
        };
      }),
    }).then((response) => GroupMemberships.fromJSON(response.members) as GroupMemberships);
  }

  resignOwnership(): Promise<Group> {
    return API.request(`/classes/${this.id}`, 'PATCH', {
      type: 'subscribed',
    }).then((group) => Group.fromJSON(group) as Group);
  }

  removeMember(relatedUser: { id: Backbone$Id }): Promise<void> {
    return API.request(`/classes/${this.id}/members/${relatedUser.id}`, 'DELETE', {});
  }

  async create(requestApprovalMessage: string | null): Promise<Group> {
    const org = this.getOrganization();
    const newGroupRequest: any = {
      group: {
        has_children: this.hasChildren(),
        class_name: this.getName(),
        name: this.getCode(),
        subscriber_initiated_chats: this.canSubscribersInitiateChats(),
        group_avatar_id: this.getAvatarId(),
        searchable: this.isSearchable(),
        organization_id: org ? org.id : null,
      },
    };
    if (requestApprovalMessage) {
      newGroupRequest.request_message = requestApprovalMessage;
    }
    const groupJson = await API.request('/classes', 'POST', newGroupRequest);
    return Group.fromJSON(groupJson) as Group;
  }

  patch(attributes: Backbone$Attrs): Promise<void> {
    return this.save(
      {},
      {
        data: attributes,
        patch: true,
      }
    );
  }

  addSubscribers(subscribers: Array<{ id: Backbone$Id }>): Promise<GroupMemberships> {
    return API.request(`/classes/${this.id}/members`, 'POST', {
      members: subscribers.map((subscriber) => {
        return {
          id: subscriber.id,
        };
      }),
    }).then((response) => GroupMemberships.fromJSON(response.members) as GroupMemberships);
  }

  /**
   * Subscribes the current user to the group
   */
  subscribe(): Promise<Group> {
    return API.request(`${this.url()}/subscription`, 'POST').then(
      (group) => Group.fromJSON(group) as Group
    );
  }

  /**
   * Unsubscribes the current user from the group
   */
  unsubscribe(): Promise<void> {
    return API.request(`${this.url()}/subscription`, 'DELETE');
  }

  toJSON(): Object {
    return this.recursivelyToJSON();
  }

  archive(archivingForReuse?: boolean): Promise<Group> {
    return API.request(`/classes/${this.id}/archive`, 'POST', {
      archive_for_reuse: !!archivingForReuse,
    }).then((group) => Group.fromJSON(group) as Group);
  }

  reuse(organizationId?: Backbone$Id, groupCode?: string, requestMessage?: string): Promise<Group> {
    return API.request(`/classes/${this.id}/reuse`, 'POST', {
      organization_id: organizationId ?? null,
      name: groupCode ?? null,
      request_message: requestMessage ?? null,
    }).then((group) => Group.fromJSON(group) as Group);
  }

  async unarchive(
    organizationId?: Backbone$Id,
    groupCode?: string,
    requestMessage?: string
  ): Promise<Group> {
    const group = await API.request(`/classes/${this.id}/unarchive`, 'POST', {
      organization_id: organizationId,
      name: groupCode || null,
      request_message: requestMessage || null,
    });
    // When we unarchive a class, query NavigationList to display newly unarchived class
    const client = getApolloClientSingleton();
    // We're not using graphql to make the query to unarchive the group so we are using the apolloClientSingleton to
    // make a refetch for the NavigationList. When apollo is updated, use client.refetchQuery instead of client.query
    await client.query<NavigationList, NavigationListVariables>({
      query: NAVIGATION_LIST,
      fetchPolicy: 'network-only',
    });
    return Group.fromJSON(group) as Group;
  }

  isArchived(): boolean {
    return !!this.get('archived_at');
  }

  getArchivedDate(): Date | undefined {
    return this.get('archived_at');
  }

  getOriginalOrganizationId(): number {
    return this.get('original_organization_id');
  }

  hasRosterSource(): boolean {
    return !!this.get('hasRosterSource');
  }

  belongsToAnOrganization(): boolean {
    return !!this.get('organization');
  }

  static parseAcceptableGroupCode(code: string): string | null {
    const matches = code.match(/[a-zA-Z0-9-]+/);
    return matches && matches.length === 1 ? matches[0] : null;
  }

  static createGroupFromCode(code: string): Group {
    return new Group({ name: code });
  }

  static fetchByCode(groupCode: string): Promise<Group | void> {
    // TODO: This should be replaced by /v2/classes/:code. We currently depend on /v2/groups/:code
    // to validate whether a group can be joined.
    return API.request(`/groups/@${groupCode}`, 'GET', {}).then(
      (group) => Group.fromJSON(group) as Group
    );
  }

  static fetchById(groupId: number): Promise<Group | void> {
    return API.request(`/classes/${groupId}`, 'GET', {}).then(
      (group) => Group.fromJSON(group) as Group
    );
  }

  static suggestCode(groupName: string): Promise<any> {
    return API.request('/groups/suggested_codes', 'GET', {
      'group[class_name]': groupName,
    });
  }

  static validateCodeUniqueness(groupCode: string): Promise<any> {
    return API.request('/groups/valid_code', 'GET', {
      'group[name]': groupCode,
    }).then((response) => {
      if (response.success) {
        return Promise.resolve(null);
      } else {
        return Promise.resolve(response);
      }
    });
  }
}
